import { runInAction } from "mobx";
import type { ChangeEvent } from "react";
import type {
  AiProductDraft,
  BoxListItem,
  IWarehouseApi,
} from "../../../api/WarehouseApi";
import {
  addProductPhoto,
  normalizeProductPhotoState,
} from "../../../domain/product-photos";
import { type INavigationStore, Screens } from "../../../stores/navigation";
import type { IStore } from "../../../stores/store";
import type { Measures } from "../../../types/entities";
import {
  applyAddProductDraft,
  buildDraftSnapshot,
  type AddProductDraft,
  type AddProductFormState,
} from "./form-state";
import { prepareImageFileDataUrl, validateImageFile } from "./media-upload";
import {
  applyAiProductDraftToState,
  applyAiAutofillDraft,
  createClearedAddProductUiState,
  loadAddProductReferenceState,
  resolveActionErrorMessage,
  submitAddProductForm,
  validateAddProductSubmit,
} from "./page-actions";

export interface AddProductWorkflowState extends AddProductFormState {
  availableBoxes: BoxListItem[];
  typeTagSuggestions: string[];
  isReferenceLoading: boolean;
  referenceError: string | null;
  isPhotoLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  voiceTranscript: string;
  isVoicePanelOpen: boolean;
  isVoiceApplying: boolean;
  voiceError: string | null;
  voiceInfo: string | null;
  measure: Measures;
}

export interface AddProductPhotoAiSuggestion {
  model: string;
  draft: AiProductDraft;
  changes: string[];
  usedTranscriptContext: boolean;
}

function resolvePhotoForAiAutofill(
  state: AddProductWorkflowState,
): string | null {
  const media = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
  });
  const primaryPhoto = media.photo.trim();
  return primaryPhoto || null;
}

function formatTextDiff(before: string, after: string): string {
  const current = before.trim() || "—";
  const next = after.trim() || "—";
  return `"${current}" → "${next}"`;
}

function formatDateDiff(before: string, after: string): string {
  const current = before.trim() || "—";
  const next = after.trim() || "—";
  return `${current} → ${next}`;
}

function formatMeasureQuantityDiff(input: {
  beforeMeasure: Measures;
  afterMeasure: Measures;
  beforeQuantity: string;
  afterQuantity: string;
}): string {
  const beforeQuantity = input.beforeQuantity.trim() || "—";
  const afterQuantity = input.afterQuantity.trim() || "—";
  return `${beforeQuantity} ${input.beforeMeasure} → ${afterQuantity} ${input.afterMeasure}`;
}

function formatTypeTags(tags: string[]): string {
  if (tags.length === 0) {
    return "—";
  }

  return tags.map((tag) => `#${tag}`).join(" ");
}

function areStringArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
}

function buildPhotoSummary(state: AddProductFormState): string {
  const normalized = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
  });
  if (normalized.photos.length === 0) {
    return "без фото";
  }

  return `${normalized.photos.length} фото`;
}

function buildProductAiSuggestionChanges(input: {
  draft: AiProductDraft;
  state: AddProductWorkflowState;
  formatParentInputValue: (value: string) => string;
}): string[] {
  const before = buildDraftSnapshot(input.state as AddProductFormState);
  const nextState: AddProductFormState = {
    ...before,
    photos: [...before.photos],
    typeTags: [...before.typeTags],
  };

  applyAiProductDraftToState({
    draft: input.draft,
    state: nextState,
    availableBoxes: input.state.availableBoxes,
    formatParentInputValue: input.formatParentInputValue,
  });

  const changes: string[] = [];

  if (before.name !== nextState.name) {
    changes.push(`Название: ${formatTextDiff(before.name, nextState.name)}`);
  }

  if (before.description !== nextState.description) {
    changes.push(
      `Описание: ${formatTextDiff(before.description, nextState.description)}`,
    );
  }

  if (!areStringArraysEqual(before.typeTags, nextState.typeTags)) {
    changes.push(
      `Теги: ${formatTypeTags(before.typeTags)} → ${formatTypeTags(nextState.typeTags)}`,
    );
  }

  if (before.parentId !== nextState.parentId) {
    changes.push(
      `Коробка: ${formatTextDiff(before.parentId, nextState.parentId)}`,
    );
  }

  if (
    before.measure !== nextState.measure ||
    before.quantityInput !== nextState.quantityInput
  ) {
    changes.push(
      `Количество: ${formatMeasureQuantityDiff({
        beforeMeasure: before.measure,
        afterMeasure: nextState.measure,
        beforeQuantity: before.quantityInput,
        afterQuantity: nextState.quantityInput,
      })}`,
    );
  }

  if (before.manufacturingDate !== nextState.manufacturingDate) {
    changes.push(
      `Дата производства: ${formatDateDiff(
        before.manufacturingDate,
        nextState.manufacturingDate,
      )}`,
    );
  }

  if (before.expiryDate !== nextState.expiryDate) {
    changes.push(
      `Срок годности: ${formatDateDiff(before.expiryDate, nextState.expiryDate)}`,
    );
  }

  if (
    before.photo !== nextState.photo ||
    !areStringArraysEqual(before.photos, nextState.photos)
  ) {
    changes.push(
      `Фото: ${buildPhotoSummary(before)} → ${buildPhotoSummary(nextState)}`,
    );
  }

  return changes;
}

export async function requestPhotoAutofillSuggestionOperation(input: {
  state: AddProductWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<AddProductPhotoAiSuggestion | null> {
  const photo = resolvePhotoForAiAutofill(input.state);
  const transcript = input.state.voiceTranscript.trim();
  const transcriptContext = transcript.length >= 4 ? transcript : "";

  if (!photo) {
    input.state.voiceError =
      "Добавьте фото товара, чтобы заполнить форму по изображению";
    return null;
  }

  runInAction(() => {
    input.state.isVoiceApplying = true;
    input.state.voiceError = null;
    input.state.voiceInfo = transcriptContext
      ? "Разбираю фото и объединяю с текстом через AI..."
      : "Разбираю фото через AI...";
  });

  try {
    const result = await input.api.fillFormDraft({
      entity: "product",
      transcript: transcriptContext || undefined,
      photo,
    });
    if (result.entity !== "product") {
      throw new Error("Некорректный тип AI-ответа для формы продукта");
    }

    const draft = result.draft as AiProductDraft;
    const changes = buildProductAiSuggestionChanges({
      draft,
      state: input.state,
      formatParentInputValue: input.formatParentInputValue,
    });

    runInAction(() => {
      input.state.voiceInfo = transcriptContext
        ? `AI подготовил правки по фото + тексту (${result.model})`
        : `AI подготовил правки по фото (${result.model})`;
    });

    return {
      model: result.model,
      draft,
      changes,
      usedTranscriptContext: !!transcriptContext,
    };
  } catch (error) {
    runInAction(() => {
      input.state.voiceError = resolveActionErrorMessage(
        error,
        "Не удалось заполнить форму по фото",
      );
    });
    return null;
  } finally {
    runInAction(() => {
      input.state.isVoiceApplying = false;
    });
  }
}

export async function setPhotoFromFileOperation(input: {
  state: AddProductWorkflowState;
  event: ChangeEvent<HTMLInputElement>;
}): Promise<void> {
  const [file] = Array.from(input.event.target.files || []);
  input.event.target.value = "";

  if (!file) {
    return;
  }

  const validationError = validateImageFile(file);
  if (validationError) {
    input.state.error = validationError;
    return;
  }

  runInAction(() => {
    input.state.isPhotoLoading = true;
    input.state.error = null;
  });

  try {
    const dataUrl = await prepareImageFileDataUrl(file);
    runInAction(() => {
      const nextState = addProductPhoto(
        {
          photo: input.state.photo,
          photos: input.state.photos,
        },
        dataUrl,
        { makePrimary: true },
      );
      input.state.photo = nextState.photo;
      input.state.photos = nextState.photos;
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить изображение";
    });
  } finally {
    runInAction(() => {
      input.state.isPhotoLoading = false;
    });
  }
}

export function applyInitialDraftOperation(input: {
  state: AddProductWorkflowState;
  initialDraft: AddProductDraft | null;
}): void {
  if (!input.initialDraft) {
    return;
  }

  applyAddProductDraft(input.state, input.initialDraft);
  const normalizedPhotos = normalizeProductPhotoState({
    photo: input.state.photo,
    photos: input.state.photos,
  });
  input.state.photo = normalizedPhotos.photo;
  input.state.photos = normalizedPhotos.photos;
}

export function applyPresetParentIdOperation(input: {
  state: AddProductWorkflowState;
  presetParentId: string;
  formatParentInputValue: (value: string) => string;
}): void {
  if (!input.presetParentId) {
    return;
  }

  input.state.parentId = input.formatParentInputValue(input.presetParentId);
}

export async function fillFromVoiceTranscriptOperation(input: {
  state: AddProductWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<void> {
  const transcript = input.state.voiceTranscript.trim();
  if (transcript.length < 4) {
    input.state.voiceError = "Нужен более подробный текст для автозаполнения";
    return;
  }
  const photo = resolvePhotoForAiAutofill(input.state);

  runInAction(() => {
    input.state.isVoiceApplying = true;
    input.state.voiceError = null;
    input.state.voiceInfo = photo
      ? "Разбираю текст и фото через AI..."
      : "Разбираю текст через AI...";
  });

  try {
    const model = await applyAiAutofillDraft({
      api: input.api,
      transcript,
      photo,
      state: input.state as AddProductFormState,
      availableBoxes: input.state.availableBoxes,
      formatParentInputValue: input.formatParentInputValue,
    });
    runInAction(() => {
      input.state.voiceInfo = `Черновик заполнен (${model})`;
    });
  } catch (error) {
    runInAction(() => {
      input.state.voiceError = resolveActionErrorMessage(
        error,
        "Не удалось заполнить форму из голосового текста",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isVoiceApplying = false;
    });
  }
}

export async function fillFromPhotoOperation(input: {
  state: AddProductWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<void> {
  const suggestion = await requestPhotoAutofillSuggestionOperation(input);
  if (!suggestion) {
    return;
  }

  if (suggestion.changes.length === 0) {
    runInAction(() => {
      input.state.voiceInfo = "AI не предложил новых изменений по фото";
    });
    return;
  }

  applyAiProductDraftToState({
    draft: suggestion.draft,
    state: input.state as AddProductFormState,
    availableBoxes: input.state.availableBoxes,
    formatParentInputValue: input.formatParentInputValue,
  });

  runInAction(() => {
    input.state.voiceInfo = suggestion.usedTranscriptContext
      ? `Черновик заполнен по фото + тексту (${suggestion.model})`
      : `Черновик заполнен по фото (${suggestion.model})`;
  });
}

export async function loadReferenceDataOperation(input: {
  state: AddProductWorkflowState;
  api: IWarehouseApi;
}): Promise<void> {
  runInAction(() => {
    input.state.isReferenceLoading = true;
    input.state.referenceError = null;
  });

  try {
    const references = await loadAddProductReferenceState({
      api: input.api,
      parentId: input.state.parentId,
    });

    runInAction(() => {
      input.state.availableBoxes = references.availableBoxes;
      input.state.typeTagSuggestions = references.typeTagSuggestions;
      input.state.parentId = references.parentId;
    });
  } catch (error) {
    runInAction(() => {
      input.state.referenceError = resolveActionErrorMessage(
        error,
        "Не удалось загрузить справочные данные",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isReferenceLoading = false;
    });
  }
}

export function clearAddProductFormOperation(input: {
  state: AddProductWorkflowState;
  presetParentId: string;
  stopVoiceListening: () => void;
}): void {
  input.stopVoiceListening();
  const nextState = createClearedAddProductUiState({
    presetParentId: input.presetParentId,
    availableBoxes: input.state.availableBoxes,
  });
  input.state.name = nextState.name;
  input.state.description = nextState.description;
  input.state.photo = nextState.photo;
  input.state.photos = nextState.photos;
  input.state.typeTags = nextState.typeTags;
  input.state.typeTagInput = nextState.typeTagInput;
  input.state.parentId = nextState.parentId;
  input.state.manufacturingDate = nextState.manufacturingDate;
  input.state.expiryDate = nextState.expiryDate;
  input.state.measure = nextState.measure;
  input.state.quantityInput = nextState.quantityInput;
  input.state.error = nextState.error;
  input.state.isPhotoLoading = nextState.isPhotoLoading;
  input.state.isVoicePanelOpen = nextState.isVoicePanelOpen;
  input.state.voiceTranscript = nextState.voiceTranscript;
  input.state.voiceError = nextState.voiceError;
  input.state.voiceInfo = nextState.voiceInfo;
}

export function openCreateBoxWithReturnOperation(input: {
  state: AddProductWorkflowState;
  navigationStore: INavigationStore;
}): void {
  input.navigationStore.setScreen(Screens.AddBox, {
    returnToAddProduct: true,
    productDraft: buildDraftSnapshot(input.state as AddProductFormState),
  });
}

export function goBackFromAddProductOperation(input: {
  presetParentId: string;
  store: IStore;
  navigationStore: INavigationStore;
}): void {
  if (input.presetParentId) {
    const box = input.store.boxes.find(
      (item) => item.id === input.presetParentId,
    );
    if (box) {
      input.navigationStore.setScreen(Screens.Box, box);
      return;
    }
  }

  input.navigationStore.setScreen(Screens.Main);
}

export async function submitAddProductOperation(input: {
  state: AddProductWorkflowState;
  api: IWarehouseApi;
  store: IStore;
  navigationStore: INavigationStore;
  onCreated?: (
    product: Awaited<ReturnType<typeof submitAddProductForm>>,
  ) => void | boolean | Promise<void | boolean>;
}): Promise<void> {
  const validationError = validateAddProductSubmit({
    state: input.state as AddProductFormState,
    availableBoxes: input.state.availableBoxes,
  });
  if (validationError) {
    input.state.error = validationError;
    return;
  }

  runInAction(() => {
    input.state.isSubmitting = true;
    input.state.error = null;
  });

  try {
    const product = await submitAddProductForm({
      api: input.api,
      state: input.state as AddProductFormState,
      availableBoxes: input.state.availableBoxes,
    });

    runInAction(() => {
      input.store.addProduct(product);
    });
    const shouldContinue = input.onCreated
      ? await input.onCreated(product)
      : undefined;
    if (shouldContinue === false) {
      return;
    }
    input.navigationStore.setScreen(Screens.Product, product);
  } catch (error) {
    runInAction(() => {
      input.state.error = resolveActionErrorMessage(
        error,
        "Не удалось создать продукт",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isSubmitting = false;
    });
  }
}
